import { AnalyticsData, PlanType } from '../types';
import { LegacyChainClient } from '../core/LegacyChainClient';

export class AnalyticsService {
  constructor(private client: LegacyChainClient) {}

  /**
   * Get analytics overview
   * Basic: Only totals and current month data
   * Advanced: Historical data, trends, and detailed breakdowns
   */
  async getOverview(): Promise<AnalyticsData> {
    const response = await this.client.makeRequest<AnalyticsData>('GET', '/analytics/overview');

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch analytics');
    }

    // Filter data based on plan
    return this.filterAnalyticsByPlan(response.data);
  }

  /**
   * Get capsule analytics
   * Basic: Count and basic stats
   * Advanced: Detailed metrics, user behavior, time patterns
   */
  async getCapsuleAnalytics(options?: {
    startDate?: Date;
    endDate?: Date;
    groupBy?: 'day' | 'week' | 'month';
  }) {
    this.checkAdvancedAnalyticsAccess();

    const params = new URLSearchParams();
    if (options?.startDate) params.append('startDate', options.startDate.toISOString());
    if (options?.endDate) params.append('endDate', options.endDate.toISOString());
    if (options?.groupBy) params.append('groupBy', options.groupBy);

    const response = await this.client.makeRequest<any>(
      'GET',
      `/analytics/capsules?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch capsule analytics');
    }

    return response.data;
  }

  /**
   * Get payment analytics (Advanced only)
   */
  async getPaymentAnalytics(options?: {
    startDate?: Date;
    endDate?: Date;
    groupBy?: 'day' | 'week' | 'month';
    currency?: string;
  }) {
    this.checkAdvancedAnalyticsAccess();

    const params = new URLSearchParams();
    if (options?.startDate) params.append('startDate', options.startDate.toISOString());
    if (options?.endDate) params.append('endDate', options.endDate.toISOString());
    if (options?.groupBy) params.append('groupBy', options.groupBy);
    if (options?.currency) params.append('currency', options.currency);

    const response = await this.client.makeRequest<any>(
      'GET',
      `/analytics/payments?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch payment analytics');
    }

    return response.data;
  }

  /**
   * Get user behavior analytics (Advanced only)
   */
  async getUserBehaviorAnalytics() {
    this.checkAdvancedAnalyticsAccess();

    const response = await this.client.makeRequest<any>('GET', '/analytics/user-behavior');

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch user behavior analytics');
    }

    return response.data;
  }

  /**
   * Get retention analytics (Advanced only)
   */
  async getRetentionAnalytics(options?: {
    cohortPeriod?: 'day' | 'week' | 'month';
    lookbackDays?: number;
  }) {
    this.checkAdvancedAnalyticsAccess();

    const params = new URLSearchParams();
    if (options?.cohortPeriod) params.append('cohortPeriod', options.cohortPeriod);
    if (options?.lookbackDays) params.append('lookbackDays', options.lookbackDays.toString());

    const response = await this.client.makeRequest<any>(
      'GET',
      `/analytics/retention?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch retention analytics');
    }

    return response.data;
  }

  /**
   * Get funnel analytics (Advanced only)
   */
  async getFunnelAnalytics(funnelId: string) {
    this.checkAdvancedAnalyticsAccess();

    const response = await this.client.makeRequest<any>(
      'GET',
      `/analytics/funnels/${funnelId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch funnel analytics');
    }

    return response.data;
  }

  /**
   * Get real-time analytics (Advanced only)
   */
  async getRealTimeAnalytics() {
    this.checkAdvancedAnalyticsAccess();

    const response = await this.client.makeRequest<any>('GET', '/analytics/realtime');

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch real-time analytics');
    }

    return response.data;
  }

  /**
   * Basic analytics: Track simple events
   */
  async trackEvent(eventName: string, properties?: Record<string, any>) {
    await this.client.makeRequest('POST', '/analytics/events', {
      event: eventName,
      properties,
      timestamp: Date.now()
    });
  }

  /**
   * Advanced analytics: Create custom dashboards
   */
  async createCustomDashboard(config: {
    name: string;
    widgets: Array<{
      type: 'chart' | 'metric' | 'table' | 'heatmap';
      dataSource: string;
      configuration: Record<string, any>;
    }>;
  }) {
    this.checkAdvancedAnalyticsAccess();

    const response = await this.client.makeRequest<any>(
      'POST',
      '/analytics/dashboards',
      config
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create dashboard');
    }

    return response.data;
  }

  /**
   * Export analytics data (Advanced only)
   */
  async exportData(options: {
    format: 'csv' | 'json' | 'excel';
    startDate: Date;
    endDate: Date;
    metrics: string[];
  }) {
    this.checkAdvancedAnalyticsAccess();

    const response = await this.client.makeRequest<{ downloadUrl: string }>(
      'POST',
      '/analytics/export',
      options
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to export analytics');
    }

    return response.data.downloadUrl;
  }

  /**
   * Get AI-powered insights (Advanced only)
   */
  async getInsights(options?: {
    focus?: 'growth' | 'retention' | 'engagement' | 'revenue';
    timeframe?: 'week' | 'month' | 'quarter';
  }) {
    this.checkAdvancedAnalyticsAccess();

    const params = new URLSearchParams();
    if (options?.focus) params.append('focus', options.focus);
    if (options?.timeframe) params.append('timeframe', options.timeframe);

    const response = await this.client.makeRequest<any>(
      'GET',
      `/analytics/insights?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch insights');
    }

    return response.data;
  }

  /**
   * Check if user has access to advanced analytics
   */
  private checkAdvancedAnalyticsAccess() {
    const planType = this.client.getPlanType();
    if (planType !== PlanType.PRO && planType !== PlanType.ENTERPRISE) {
      throw new Error('Advanced analytics requires Pro or Enterprise plan');
    }
  }

  /**
   * Filter analytics data based on plan
   */
  private filterAnalyticsByPlan(data: AnalyticsData): AnalyticsData {
    const planType = this.client.getPlanType();
    
    // Basic analytics (Starter plan) - only current data
    if (planType === PlanType.STARTER) {
      return {
        ...data,
        monthlyGrowth: 0, // Hide growth metrics
        topCategories: [] // Hide detailed breakdowns
      };
    }

    // Free plan - minimal data
    if (planType === PlanType.FREE) {
      return {
        totalCapsules: data.totalCapsules,
        totalPayments: 0,
        totalDocuments: 0,
        storageUsed: data.storageUsed,
        activeUsers: 0,
        monthlyGrowth: 0,
        topCategories: []
      };
    }

    // Pro and Enterprise - full data
    return data;
  }
} 